import { useQuery } from '@tanstack/react-query';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { businessUnitsApi } from '@/lib/api';
import { useCanHandleRole } from '@/lib/business-units';
import { queryKeys } from '@/lib/query-keys';

interface Props {
  /** The unit whose roles are offered. Nothing is offered until it is set. */
  businessUnitId: string | null;
  value: string | null;
  onChange: (systemRoleId: string) => void;
  disabled?: boolean;
  /** Accessible name / test hook. */
  'aria-label'?: string;
  className?: string;
}

/**
 * A choice of ONE business unit's roles — what a person acts as in that unit.
 * A unit's roles are its own (two units may each have a "User"), so the list
 * is always the chosen unit's, never the tenant's.
 */
export function UnitRoleSelect({
  businessUnitId,
  value,
  onChange,
  disabled,
  className,
  ...aria
}: Props) {
  const canHandle = useCanHandleRole();
  const roles = useQuery({
    queryKey: queryKeys.businessUnits.roles(businessUnitId ?? ''),
    queryFn: () => businessUnitsApi.roles(businessUnitId as string),
    enabled: Boolean(businessUnitId),
  });
  return (
    <Select
      value={value ?? undefined}
      onValueChange={onChange}
      disabled={disabled || !businessUnitId || roles.isLoading}
    >
      <SelectTrigger className={className ?? 'w-full'} aria-label={aria['aria-label'] ?? 'Role'}>
        <SelectValue
          placeholder={
            !businessUnitId
              ? 'Choose a Business Unit first'
              : roles.isLoading
                ? 'Loading…'
                : 'Choose a role'
          }
        />
      </SelectTrigger>
      <SelectContent>
        {/* Only roles the caller may hand out (restricted permissions they hold). */}
        {(roles.data?.data ?? [])
          .filter((r) => canHandle(r) || r.id === value)
          .map((r) => (
            <SelectItem key={r.id} value={r.id}>
              {r.name}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  );
}
